import React from 'react';
import Button from '../../../components/ui/Button';

const SecuritySection = ({
  user,
  onPasswordReset,
  isSubmitting
}) => {
  const formatLastLogin = (dateString) => {
    if (!dateString) return 'Never';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Less than an hour ago';
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    
    return date?.toLocaleDateString();
  };

  return (
    <div className="bg-white rounded-lg border border-border shadow-sm p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-foreground mb-2">
          Security & Access
        </h3>
        <p className="text-sm text-muted-foreground">
          Manage password and security settings
        </p>
      </div>
      <div className="space-y-6">
        {/* Password Management */}
        <div>
          <h4 className="text-sm font-medium text-foreground mb-3">Password Management</h4>
          <div className="space-y-3">
            <Button
              variant="outline"
              onClick={onPasswordReset}
              disabled={isSubmitting}
              iconName="Key"
              iconPosition="left"
              className="w-full"
            >
              {isSubmitting ? 'Sending...' : 'Reset Password'}
            </Button>
            <p className="text-xs text-muted-foreground">
              Sends a password reset link to the user's email address
            </p>
          </div>
        </div>

        {/* Last Login Information */}
        <div>
          <h4 className="text-sm font-medium text-foreground mb-3">Login Activity</h4>
          <div className="bg-muted/30 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Last Login</span>
              <span className="text-sm text-muted-foreground">
                {formatLastLogin(user?.lastLogin)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Account Created</span>
              <span className="text-sm text-muted-foreground">
                {user?.createdAt ? new Date(user.createdAt)?.toLocaleDateString() : 'Unknown'}
              </span>
            </div>
          </div>
        </div>

        {/* Login History */}
        {user?.loginHistory && user?.loginHistory?.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-foreground mb-3">Recent Login History</h4>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {user?.loginHistory?.slice(0, 3)?.map((login, index) => (
                <div key={index} className="bg-muted/20 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium">
                      {new Date(login?.date)?.toLocaleDateString()}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(login?.date)?.toLocaleTimeString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>IP: {login?.ip}</span>
                    <span>{login?.location}</span>
                  </div>
                </div>
              ))}
            </div>
            {user?.loginHistory?.length > 3 && (
              <p className="text-xs text-muted-foreground mt-2">
                And {user?.loginHistory?.length - 3} more login sessions...
              </p>
            )}
          </div>
        )}

        {/* Account Lockout Controls */}
        <div>
          <h4 className="text-sm font-medium text-foreground mb-3">Account Security</h4>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <svg className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Security Status</p>
                <p className="text-xs">
                  Account is secure with no failed login attempts or security alerts.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Audit Trail Note */}
        <div className="border-t border-border pt-4">
          <div className="flex items-start space-x-2">
            <svg className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <div className="text-xs text-muted-foreground">
              <p className="font-medium mb-1">Audit Trail</p>
              <p>
                All security actions and profile changes are logged for compliance and audit purposes.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecuritySection;